import React from 'react';
import ConfigMenu from '../components/ConfigMenu';
import { 
  FileText, 
  Calculator, 
  TrendingUp, 
  Printer, 
  ChevronRight, 
  Settings2, 
  ArrowLeft,
  ShieldCheck,
  Landmark
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Card de Configuración Técnica - Diseño Enterprise
 */
const ConfigCard = ({ icon: Icon, title, desc, colorClass, bgClass, onClick }) => (
  <div
    className="group bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-[#51A7FD] hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick?.(); }}
  >
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${bgClass} ${colorClass} transition-colors shrink-0`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0 pr-4">
        <h6 className="text-[11px] font-bold text-slate-800 uppercase tracking-widest mb-1 group-hover:text-[#51A7FD] transition-colors">
          {title}
        </h6>
        <p className="text-[10px] text-slate-400 font-medium leading-relaxed italic">
          {desc}
        </p>
      </div>
    </div>
    <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
      <ChevronRight size={16} className="text-[#51A7FD]" />
    </div>
  </div>
);

/**
 * Módulo de Configuración de Finanzas - Versión Enterprise Unificada
 */
function ConfigFinanzas() {
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] font-sans text-slate-700 p-4 md:p-6 lg:p-8">
      
      {/* HEADER TÉCNICO */}
      <header className="flex items-center gap-4 mb-10 px-2">
        <button 
          onClick={() => navigate('/sistema')}
          className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 uppercase tracking-tight leading-none">Configuración del Sistema</h1>
          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-2 italic">Parámetros técnicos de la organización</p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-[1600px] mx-auto animate-nova-in">
        
        {/* COLUMNA IZQUIERDA: MENÚ DE NAVEGACIÓN */}
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-2">
            <ConfigMenu />
          </div>
        </aside>

        {/* COLUMNA DERECHA: GRILLA DE CONFIGURACIÓN */}
        <main className="lg:col-span-9 space-y-8">
          <div className="flex items-center gap-3 px-2">
            <Landmark size={18} className="text-[#51A7FD]" />
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em]">Finanzas e Impuestos</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <ConfigCard 
              icon={FileText} 
              title="Tipos de Comprobante" 
              desc="Configuración de facturas A/B/C, remitos de entrega y protocolos de numeración." 
              onClick={() => navigate("/sistema/config-facturacion")}
              colorClass="text-[#51A7FD]" 
              bgClass="bg-blue-50"
            />
            <ConfigCard 
              icon={Calculator} 
              title="Reglas de Facturación" 
              desc="Límites fiscales, integración con AFIP, puntos de venta y certificados digitales." 
              onClick={() => navigate("/sistema/config-facturacion")}
              colorClass="text-orange-500" 
              bgClass="bg-orange-50"
            />
            <ConfigCard 
              icon={TrendingUp} 
              title="Estrategia de Costos" 
              desc="Configuración de drivers de distribución, márgenes de reposición y prorrateo técnico." 
              onClick={() => navigate("/sistema/config-finanzas-impuestos/costos")}
              colorClass="text-[#F05A57]" 
              bgClass="bg-red-50"
            />
            <ConfigCard 
              icon={Printer} 
              title="Reportes Impresos" 
              desc="Diseño técnico de comprobantes PDF, gestión de logos y leyendas legales obligatorias." 
              onClick={() => navigate("/sistema/config-finanzas-impuestos/reportes-impresos")}
              colorClass="text-slate-600" 
              bgClass="bg-slate-100"
            />
            <ConfigCard 
              icon={ShieldCheck} 
              title="Protocolos de IVA" 
              desc="Gestión de alícuotas automáticas por categoría y regímenes de retención." 
              onClick={() => navigate("/sistema/config-facturacion")}
              colorClass="text-emerald-600" 
              bgClass="bg-emerald-50"
            />
          </div>
        </main>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default ConfigFinanzas;
